import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TextField, Typography, Box, Autocomplete, CircularProgress } from '@mui/material';
import ParticipantForm from './ParticipantForm';
import { API_BASE_URL } from '../api/contests';
import { debounce } from 'lodash'
import PassphraseCheck from './PassphraseCheck';

interface Participant {
  participant_id: number;
  name: string;
}

const UpdateParticipant: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>(''); // For storing search input
  const [participants, setParticipants] = useState<Participant[]>([]); // Search results
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null); // Selected participant to edit
  const [participantData, setParticipantData] = useState<any>(null); // Data to populate the form
  const [loading, setLoading] = useState<boolean>(false); // To handle loading state for search
  const [accessGranted, setAccessGranted] = useState(false);
  
  // Debounced function to handle search
  const debouncedSearch = debounce(async (searchTerm: string) => {
    if (!searchTerm) {
      setParticipants([]); // Clear participants if search term is empty
      return;
    }

    setLoading(true); // Set loading before fetching

    try {
      const response = await axios.get(`${API_BASE_URL}/participants/search?q=${searchTerm}`);
      setParticipants(response.data);
    } catch (error) {
      console.error('Error fetching participants:', error);
    } finally {
      setLoading(false); // Set loading to false after fetching
    }
  }, 500, {trailing: true, leading: false}); // Delay search by 500ms

  useEffect(() => {
    // Trigger debounced search when searchTerm changes
    debouncedSearch(searchTerm);

    // Cleanup debounce on unmount
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm]);

  const handleSelectParticipant = async (participant: Participant) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/participants/${participant.participant_id}`);
      setSelectedParticipant(participant); // Store selected participant
      setParticipantData(response.data); // Load participant data into form
    } catch (error) {
      console.error('Error fetching participant data:', error);
    }
  };

  const handleSuccess = () => {
    setSelectedParticipant(null); // Clear the selection after a successful update
    setParticipantData(null);
    setSearchTerm('');
  };

  return (
    !accessGranted ? <PassphraseCheck onAccessGranted={() => setAccessGranted(true)} />
    :<Box>
      <Typography variant="h5" gutterBottom>
        Update Participant
      </Typography>

      {/* Search Box */}
      <Autocomplete
        freeSolo
        options={participants.map((participant) => participant.name)}
        inputValue={searchTerm}
        onInputChange={(event, newInputValue) => {
          setSearchTerm(newInputValue);
        }}
        onChange={(event, value) => {
          const selected = participants.find((p) => p.name === value);
          if (selected) handleSelectParticipant(selected);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search Participant"
            variant="outlined"
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            style={{ marginBottom: '16px' }}
          />
        )}
      />

      {/* Display form once a participant is selected */}
      {selectedParticipant && participantData ? (
        <ParticipantForm
          participantId={selectedParticipant.participant_id}
          initialData={participantData}
          onSubmitSuccess={handleSuccess}
        />
      ) : (
        <Typography variant="body2" color="textSecondary">
          Search for a participant to update.
        </Typography>
      )}
    </Box>
  );
};

export default UpdateParticipant;
